import React, { useRef, useEffect, useState, forwardRef, memo } from 'react'
import styled from '@emotion/styled'
import { useGetResizer } from '@/contexts/Resizer'
import {
  useViewportScroll,
  motion,
  useTransform,
  useSpring,
} from 'framer-motion'

import { graphTitle } from '@/styles/typography'

import { grid, screen, spacing } from '@/styles/mixins'
import { useSection } from '@/contexts/SectionContext'
import { easing } from '@/util/math'

import { StaticImage } from 'gatsby-plugin-image'

import Sharer from '@/components/UI/Sharer'

const transition = {
  duration: 0.5,
  ease: [0.43, 0.13, 0.23, 0.96],
}

const titleVariants = {
  exit: { opacity: 0, transition },
  enter: {
    opacity: 1,
    transition,
  },
}

const Container = styled.div`
  min-height: 100vh;
  width: 100%;
  position: relative;
`

const HeightWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const Screen = styled.div`
  position: sticky;
  height: 100vh;
  top: 0;
  width: 100%;
`

const Wrapper = styled.div`
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  ${grid};
  text-align: center;
  align-items: center;
  justify-items: center;
`

const Bg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  .gatsby-image-wrapper{
    height: 100vh;
  }
  .desktop {
    display: block;
    @media (max-width: 768px) {
      display: none;
    }
  }
  .mobile {
    display: none;
    @media (max-width: 768px) {
      display: block;
    }
  }
}
`

const TitleWrapper = styled(motion.div)`
  grid-column: 3 / -3;
  ${spacing('marginTop', 'l')}
  ${spacing('marginBottom', 'l')}
  position: relative;
  z-index: 2;
  @media ${screen('md')} {
    grid-column: 7 / -7;
  }
`

const Title = styled.h1`
  font-size: 12rem;
  line-height: 1;
  text-shadow: 6px 6px 0 #fd9f01;
  @media (max-width: 1400px) {
    font-size: 10rem;
  }
  @media (max-width: 1200px) {
    font-size: 6rem;
  }
  @media (max-width: 900px) {
    font-size: 4rem;
  }
`

const Line1 = styled(motion.span)`
  position: relative;
  z-index: -1;
  display: block;
  will-change: transform, opacity;
  font-size: 4rem;
  span {
    font-size: 0.99em;
    letter-spacing: -0.05em;
  }
`

const Line2 = styled(motion.span)`
  position: relative;
  display: block;
  will-change: transform, opacity;

  span {
    font-family: 'Work Sans', Arial, Helvetica, sans-serif;
    font-size: 0.99em;
    letter-spacing: -0.05em;
  }
`

const Subwrap = styled.div`
  ${spacing('marginBottom', 'l')}
  max-width: 700px;
  margin-left: auto;
  margin-top: 1rem;
  margin-right: auto;
`

const Subtitle = styled(motion.p)`
  font-family: 'Work Sans', Arial, Helvetica, sans-serif;
  /* font-weight: 600; */
  ${graphTitle};

  color: white;
  will-change: transform, opacity;
  display: inline;
  background-color: rgba(0, 0, 0, 0.35);
  padding: 0.2rem;
  line-height: 1.4;
  text-shadow: 3px 3px 3px rgba(0, 0, 0, 0.5);
`

const Cover = memo(
  forwardRef(({ titleLine1, titleLine2, subtitle, refTop, refBottom }, ref) => {
    const [windowHeight, setWindowHeight] = useState(768)
    let setActiveSection = useSection()

    useEffect(() => {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setActiveSection('')
            }
          })
        },
        { rootMargin: '-50% 0px -50%' },
      )
      observer.observe(ref.current)
      return () => {
        observer.disconnect()
      }
    }, [setActiveSection, ref])

    const height = 2.5
    const { scrollY } = useViewportScroll()

    const titleProgressY = useSpring(
      useTransform(
        scrollY,
        [refTop + (refBottom - refTop) * 0.55, refBottom],
        [0, -1 * (refTop + (refBottom - refTop) * 0.35)],
        {
          ease: easing.inQuad,
        },
      ),
      {
        stiffness: 1000,
        damping: 50,
      },
    )

    const titleProgressOpacity = useSpring(
      useTransform(
        scrollY,
        [refTop + (refBottom - refTop) - windowHeight * 0.5, refBottom],
        [1, 0],
      ),
      {
        stiffness: 1000,
        damping: 50,
      },
    )

    useEffect(() => {
      const handleResize = () => {
        updateWindowHeight()
      }

      const updateWindowHeight = () => {
        setWindowHeight(window.innerHeight)
      }

      updateWindowHeight()
      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    return (
      <Container ref={ref} style={{ height: `${height * 100}vh` }}>
        <HeightWrapper style={{ height: `${height * 200}vh` }}>
          <Screen style={{ marginBottom: `${height * 100}vh` }}>
            <Wrapper
              style={{
                height: `${100 + height * 100}vh`,
                paddingBottom: `${height * 100}vh`,
              }}>
              <TitleWrapper>
                <Title variants={titleVariants}>
                  <Line1
                    dangerouslySetInnerHTML={{ __html: titleLine1 }}
                    style={{ y: titleProgressY, opacity: titleProgressOpacity }}
                  />
                  <Line2
                    dangerouslySetInnerHTML={{ __html: titleLine2 }}
                    style={{ y: titleProgressY, opacity: titleProgressOpacity }}
                  />
                </Title>
                <Subwrap>
                  <Subtitle
                    variants={titleVariants}
                    dangerouslySetInnerHTML={{ __html: subtitle }}
                    style={{ y: titleProgressY, opacity: titleProgressOpacity }}
                  />
                </Subwrap>
                <Sharer
                  url={`https://especiales.latercera.com/100-anos-de-la-insulina/`}
                  title="100 años de la insulina - Especiales La Tercera - Presentado por Novo Nordisk"
                  comparte="comparte"
                  hashtags="insulina100años"
                  via="latercera"
                />
              </TitleWrapper>
              <Bg>
                <div className="mobile">
                  <StaticImage alt="" src="./intro-mobile.jpg" />
                </div>
                <div className="desktop">
                  <StaticImage alt="" src="./intro.jpg" />
                </div>
              </Bg>
            </Wrapper>
          </Screen>
        </HeightWrapper>
      </Container>
    )
  }),
)

const HeroComponent = props => {
  const ref = useRef()
  const area = useGetResizer()
  const [refTop, setRefTop] = useState(0)
  const [refBottom, setRefBottom] = useState(1)

  useEffect(() => {
    const refPosition = ref.current?.getBoundingClientRect()
    const top = refPosition.top + window.pageYOffset
    const bottom = top + refPosition.height - window.innerHeight

    setRefTop(top)
    setRefBottom(bottom)
  }, [area])

  return <Cover {...props} ref={ref} refTop={refTop} refBottom={refBottom} />
}

export default HeroComponent
